import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Container, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import { ConfirmationNumberOutlined, Login } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import EventList from './EventList';
import InvitationLinkAccess from './InvitationLinkAccess';

export const LoginLink: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.delete('code');
  const search = params.toString();
  const next = `${location.pathname}${search ? `?${search}` : ''}${location.hash}`;
  return <Button component="a" href={`/api/auth/login?returnTo=${encodeURIComponent(next)}`} variant="outlined" fullWidth startIcon={<Login />}>
    Login with RyderTech SSO
  </Button>;
};

const LoadingAccess = () => <Box role="status" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, py: 8 }}>
  <CircularProgress size={22} /><Typography color="text.secondary">Checking access…</Typography>
</Box>;

const SessionStatus: React.FC = () => {
  const { refresh } = useAuth();
  return <Alert severity="error" action={<Button color="inherit" onClick={refresh}>Try again</Button>}>
    Unable to check your session. Please try again.
  </Alert>;
};

const responseStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status;

const InvitationPrompt: React.FC<{ slug?: string; onAccess?: () => void }> = ({ slug, onAccess }) => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const pending = useRef(false);
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (pending.current) return;
    if (!code.trim()) { setError('Enter your invitation code.'); return; }
    pending.current = true;
    setSubmitting(true);
    setError('');
    try {
      const { data } = await axios.post('/api/invitations/redeem', { code: code.trim(), ...(slug ? { slug } : {}) });
      if (!data || typeof data.slug !== 'string' || !/^[a-zA-Z0-9_-]+$/.test(data.slug) || (slug && data.slug !== slug)) {
        throw { response: { status: 403 } };
      }
      if (!active.current) return;
      setCode('');
      if (slug && onAccess) onAccess();
      else navigate(`/view/events/${encodeURIComponent(data.slug)}`);
    } catch (error) {
      if (!active.current) return;
      const status = responseStatus(error);
      setError(status === 429
        ? 'Too many attempts. Please wait a few minutes and try again.'
        : status && [400, 401, 403, 404].includes(status)
          ? 'That invitation code is not valid. Check the code with your event organizer and try again.'
          : 'Unable to check your invitation code. Please try again.');
    } finally {
      pending.current = false;
      if (active.current) setSubmitting(false);
    }
  };

  return <Box sx={{ maxWidth: 440, mx: 'auto', py: { xs: 2, sm: 5 } }}>
    <Paper sx={{ p: { xs: 3, sm: 4 } }}>
      <Box sx={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.selected', color: 'primary.main', borderRadius: 2, mb: 2.5 }}>
        <ConfirmationNumberOutlined />
      </Box>
      <Typography variant="h2" component="h1" sx={{ mb: 1 }}>You're invited</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {slug ? 'Enter your invitation code to open this event.' : 'Enter your invitation code to view your event and RSVP.'}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          <TextField fullWidth label="Invitation code" name="invitation_code" value={code} required
            onChange={event => { setCode(event.target.value); if (error) setError(''); }}
            autoComplete="off" inputProps={{ autoCapitalize: 'none', spellCheck: false, maxLength: 128 }}
            helperText="Your event organizer shared this code with you." error={Boolean(error)} disabled={submitting} />
          {error && <Alert severity="error">{error}</Alert>}
          <Button type="submit" fullWidth variant="contained" disabled={submitting}>{submitting ? 'Checking…' : 'Submit'}</Button>
        </Stack>
      </Box>
      <Divider sx={{ my: 3 }} />
      {session?.authenticated && !session.can_manage && <Alert severity="info" sx={{ mb: 2 }}>
        Your account does not have Forms access. You can still use an invitation code to RSVP.
      </Alert>}
      <LoginLink />
    </Paper>
  </Box>;
};

export const HomePage: React.FC = () => {
  const { session, loading, error, accessVersion } = useAuth();
  if (loading) return <LoadingAccess />;
  if (error) return <SessionStatus />;
  return session?.can_manage ? <EventList /> : <InvitationPrompt key={accessVersion} />;
};

export const RequireForms: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { session, loading, error } = useAuth();
  if (loading) return <LoadingAccess />;
  if (error) return <SessionStatus />;
  if (session?.can_manage) return <>{children}</>;
  return <Paper sx={{ p: { xs: 3, sm: 4 }, maxWidth: 440, mx: 'auto' }}>
    <Typography variant="h4" component="h1" sx={{ mb: 1 }}>Manage events</Typography>
    <Typography color="text.secondary" sx={{ mb: 3 }}>
      {session?.authenticated ? 'Your account needs the Forms group to create or manage events.' : 'Log in with your RyderTech account to create or manage events.'}
    </Typography>
    <LoginLink />
    <Button component={RouterLink} to="/" fullWidth sx={{ mt: 1 }}>Back to home</Button>
  </Paper>;
};

// An edit capability does not require an event invitation, but its mutations
// still need a discovered anonymous/authenticated session and CSRF token.
export const RequireSession: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { session, loading, error } = useAuth();
  if (!loading && !error && session) return <>{children}</>;
  return <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>{error ? <SessionStatus /> : <LoadingAccess />}</Container>
  </Box>;
};

const ScopedEventAccess: React.FC<React.PropsWithChildren<{ slug: string; standalone: boolean }>> = ({ slug, children, standalone }) => {
  const { session, loading, error } = useAuth();
  const [allowed, setAllowed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [checkError, setCheckError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (loading || error || session?.can_manage) return;
    let active = true;
    setChecking(true);
    setCheckError(false);
    axios.get(`/api/events/${encodeURIComponent(slug)}/access`).then(({ data }) => {
      if (active) setAllowed(data?.allowed === true);
    }).catch(error => {
      if (active) {
        setAllowed(false);
        setCheckError(![401, 403, 404].includes(responseStatus(error) || 0));
      }
    }).finally(() => { if (active) setChecking(false); });
    return () => { active = false; };
  }, [slug, session?.can_manage, loading, error, attempt]);

  const frame = (content: React.ReactNode) => standalone
    ? <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}><Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>{content}</Container></Box>
    : content;
  if (loading) return <>{frame(<LoadingAccess />)}</>;
  if (error) return <>{frame(<SessionStatus />)}</>;
  if (session?.can_manage || allowed) return <>{children}</>;
  if (checking) return <>{frame(<LoadingAccess />)}</>;
  if (checkError) return <>{frame(<Alert severity="error" action={<Button color="inherit" onClick={() => setAttempt(value => value + 1)}>Try again</Button>}>
    Unable to check event access. Please try again.
  </Alert>)}</>;
  return <>{frame(<InvitationPrompt slug={slug} onAccess={() => setAllowed(true)} />)}</>;
};

export const EventAccess: React.FC<React.PropsWithChildren<{ standalone?: boolean }>> = ({ children, standalone = false }) => {
  const { slug } = useParams<{ slug: string }>();
  const { accessVersion } = useAuth();
  return <InvitationLinkAccess key={slug} slug={slug || ''}>
    <ScopedEventAccess key={`${slug}:${accessVersion}`} slug={slug || ''} standalone={standalone}>{children}</ScopedEventAccess>
  </InvitationLinkAccess>;
};
